import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GAP = 8;
const MARGIN = 12;

// Fixed-position coordinates for a popover relative to its anchor, flipped
// above when there is no room below and clamped to the viewport.
export function computePopoverPosition(anchor, preferredWidth) {
  const r = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(preferredWidth, vw - MARGIN * 2);
  let left = r.left + r.width / 2 - width / 2;
  left = Math.max(MARGIN, Math.min(left, vw - width - MARGIN));
  const arrowX = Math.max(14, Math.min(r.left + r.width / 2 - left, width - 14));
  const below = r.bottom + GAP;
  const spaceBelow = vh - below;
  const up = spaceBelow < 260 && r.top > spaceBelow;
  return {
    width,
    left,
    arrowX,
    up,
    top: up ? undefined : below,
    bottom: up ? vh - r.top + GAP : undefined,
    maxHeight: Math.max(160, (up ? r.top - GAP : spaceBelow) - MARGIN),
  };
}

/**
 * Hover / click popover, portalled to <body> so it is never clipped by scroll
 * containers (tables, cards).
 *
 * Hovering or focusing the trigger shows the popover; clicking the trigger
 * pins it open (so links inside can be clicked); clicking again, pressing
 * Escape or clicking outside closes it.
 *
 *   <Popover
 *     trigger={(props, { open, pinned }) => <button {...props}>…</button>}
 *     label="Terminated clients"
 *   >
 *     …content…
 *   </Popover>
 *
 * `trigger` receives the props it must spread onto the anchor element (ref +
 * mouse / focus / click handlers + aria-expanded). `children` may be a node
 * or a function receiving { pinned, close }.
 */
export default function Popover({ trigger, children, label, width = 320, className = "", popClassName = "", disabled = false }) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const closeTimer = useRef(null);

  const open = !disabled && (hover || pinned);

  // position (and re-position on scroll / resize while open)
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const update = () => btnRef.current && setPos(computePopoverPosition(btnRef.current, width));
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, width]);

  useEffect(() => {
    if (!pinned) return undefined;
    const onDoc = (e) => {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) setPinned(false);
    };
    const onKey = (e) => e.key === "Escape" && setPinned(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned]);

  const show = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHover(true);
  }, []);
  // small grace period so the pointer can travel from the trigger to the popover
  const hide = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHover(false), 180);
  }, []);
  useEffect(() => () => closeTimer.current && clearTimeout(closeTimer.current), []);

  const close = useCallback(() => {
    setPinned(false);
    setHover(false);
  }, []);

  const triggerProps = {
    ref: btnRef,
    "aria-expanded": open,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: () => !pinned && hide(),
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setPinned((p) => !p);
    },
  };

  return (
    <span className={`bd-pop-anchor ${pinned ? "is-pinned" : ""} ${open ? "is-open" : ""} ${className}`} onClick={(e) => e.stopPropagation()}>
      {trigger(triggerProps, { open, pinned })}
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            className={`bd-pop ${pos.up ? "bd-pop--up" : ""} ${popClassName}`}
            role="dialog"
            aria-label={label}
            style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, "--arrow-x": `${pos.arrowX}px` }}
            onMouseEnter={show}
            onMouseLeave={hide}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {typeof children === "function" ? children({ pinned, close }) : children}
          </div>,
          document.body
        )}
    </span>
  );
}
